import { msalInstance, hubApiScope } from '../features/auth/msalConfig';

import { getAppConfig } from './config';

export interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
  logoUrl: string | null;
  loginPath?: string;
  loginUrl?: string;
}

export interface TenantWithLoginUrl extends Tenant {
  loginUrl: string;
}

/**
 * Every real Customer Zero tenant name shares the literal prefix "CUSTOMER ZERO - SSNC"
 * (e.g. "CUSTOMER ZERO - SSNC - Advent UK"), which is useless for telling tiles apart in the
 * picker and collapses every Avatar's initial down to the same "C". Strips it for display only
 * — `tenant.name` itself is untouched, so search/aria-label still see the full account name.
 */
const SHARED_TENANT_PREFIX = /^CUSTOMER ZERO\s*-\s*SSNC\s*-?\s*/i;

export function getTenantDisplayName(name: string): string {
  const stripped = name.replace(SHARED_TENANT_PREFIX, '').trim();

  return stripped || name;
}

export function buildLoginUrl(tenant: Tenant): string {
  if (tenant.loginUrl) {
    return tenant.loginUrl;
  }

  const path = tenant.loginPath || '/login';

  const { tenantProtocol, tenantBaseDomain } = getAppConfig();

  return `${tenantProtocol}://${tenant.subdomain}.${tenantBaseDomain}${path}`;
}

async function getAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('No authenticated account available to request an access token for');
  }

  const result = await msalInstance.acquireTokenSilent({
    scopes: [hubApiScope],
    account,
  });

  return result.accessToken;
}

export async function fetchTenants(): Promise<TenantWithLoginUrl[]> {
  const headers: Record<string, string> = {};

  if (!getAppConfig().demoMode) {
    const accessToken = await getAccessToken();
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch('/api/tenants', { headers });

  if (!response.ok) {
    throw new Error(`Failed to load tenants (status ${response.status})`);
  }

  const data: { tenants: Tenant[] } = await response.json();

  return data.tenants
    .map((tenant) => ({ ...tenant, loginUrl: buildLoginUrl(tenant) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
